import type { StreamCandidate, StreamKind } from './engine';

const NATIVE_EXTENSIONS = new Set(['mp4', 'm4v', 'mov', 'webm', 'mkv', 'mp3', 'aac', 'ogg', 'ogv']);
/** Xtream-style live path: /live/<user>/<pass>/<id>.<ext> */
const XTREAM_LIVE = /^(.*\/live\/[^/]+\/[^/]+\/\d+)\.(ts|m3u8)$/i;

function extensionOf(url: string): string {
  try {
    const path = new URL(url).pathname;
    const last = path.slice(path.lastIndexOf('/') + 1);
    const dot = last.lastIndexOf('.');
    return dot > 0 ? last.slice(dot + 1).toLowerCase() : '';
  } catch {
    return '';
  }
}

export function streamKindOf(url: string): StreamKind | null {
  const extension = extensionOf(url);
  if (extension === 'm3u8' || extension === 'm3u') return 'hls';
  if (extension === 'ts' || extension === 'mpegts') return 'ts';
  if (NATIVE_EXTENSIONS.has(extension)) return 'native';
  return null;
}

/**
 * Ordered list of ways to play an item. The first entry is the provider's
 * URL. After it come fallbacks the player tries when that one keeps failing:
 * - Xtream live streams are offered as both MPEG-TS and HLS by the panel;
 * - an extension-less live URL may be either, so both engines are tried.
 * `alternates` are other URLs for the same content (e.g. duplicate entries).
 */
export function playbackCandidates(url: string, live: boolean, alternates: string[] = []): StreamCandidate[] {
  const candidates: StreamCandidate[] = [];
  const add = (candidate: StreamCandidate) => {
    if (!candidates.some((existing) => existing.url === candidate.url && existing.kind === candidate.kind)) candidates.push(candidate);
  };
  for (const source of [url, ...alternates]) {
    const kind = streamKindOf(source);
    if (kind) {
      add({ url: source, kind });
    } else if (live) {
      add({ url: source, kind: 'ts' });
      add({ url: source, kind: 'hls' });
    } else {
      add({ url: source, kind: 'native' });
    }
    const xtream = live ? XTREAM_LIVE.exec(source) : null;
    if (xtream) {
      const base = xtream[1] as string;
      const other = xtream[2]?.toLowerCase() === 'ts' ? 'm3u8' : 'ts';
      add({ url: `${base}.${other}`, kind: other === 'ts' ? 'ts' : 'hls' });
    }
  }
  return candidates;
}
